
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import Table from '../components/table/table';
import Input from '../components/input/Input';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { getClients } from '../services/ClientsService';
import { ITableData } from '../types/interfaces';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Span from '../components/texts/span/Span';
import Tooltip from '../components/tooltip/Tooltip';
import TruncatedTextWithTooltip from '../components/TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import { routes } from '../routes/routePaths';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import FilterModal from '../components/modals/FilterModal';
import Spinner from '../components/Spinner/spinner';
import { getFilters } from '../services/ApplicationService';
import { FilterData } from '../components/modals/FilterModal/FilterModal.types';
import { normalizeCuitForSearch } from '../utils/Utils';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import { useConfig } from '../context/ConfigContext';
import { buildActivityOrRubroColumn } from './clientListColumns';
import { useRestoreListState } from '../hooks/useRestoreListState';
import { buildListState, DASHBOARD_KEYS } from '../utils/listNavigationState';

const Clients = () => {
  const name = localStorage.getItem('name') || '';
  const navigate = useNavigate();
  const { label: fiscalIdLabel, searchPlaceholder, format: formatFiscalId } =
    useFiscalIdConfig();
  const { isUruguayEnabled } = useConfig();
  const restoredList = useRestoreListState(DASHBOARD_KEYS.clients);
  const [clients, setClients] = useState<any[]>([]);
  const [tableData, setTableData] = useState<ITableData>({ page: 0, size: 10, totalElements: 0, totalPages: 0 });
  const [page, setPage] = useState<number>(restoredList.page);
  const [q, setQ] = useState<string>(restoredList.searchText);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    restoredList.activeFilters,
  );
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [isClearFiltersHovered, setIsClearFiltersHovered] = useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<{
    field: string | null;
    direction: 'asc' | 'desc' | null;
  }>(restoredList.sortConfig);
  const lastRequest = useRef(0);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await getFilters('client');
        if (response.success && 'data' in response) {
          setFilterData(response.data);
        }
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    };
    loadFilters();
  }, []);

  // Effect para manejar cambios de página (búsqueda inmediata)
  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line
  }, [page]);

  useEffect(() => {
    // Limpiar timeout anterior si existe
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Si hay un término de búsqueda o está vacío (para mostrar todos), configurar el timeout
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetear a la primera página cuando se busca
      fetchClients();
    }, 500);

    // Cleanup function para limpiar el timeout cuando el componente se desmonte
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line
  }, [q, JSON.stringify(activeFilters)]);

  // Effect para manejar cambios de ordenamiento
  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line
  }, [sortConfig.field, sortConfig.direction]);

  const fetchClients = async () => {
    setIsSearching(true);
    const requestId = Date.now();
    lastRequest.current = requestId;
    try {
      const cleanQ = normalizeCuitForSearch(q);
      
      const res = await getClients({ 
        page: page, 
        size: tableData.size, 
        q: cleanQ,
        referrer: activeFilters.referrerName || '',
        commercialId: activeFilters.commercialId || '',
        producerId: activeFilters.producerId || '',
        status: activeFilters.status || '',
        sortField: sortConfig.field || '',
        sortDirection: sortConfig.direction || ''
      });
      if (lastRequest.current !== requestId) return;
      if (res && res.success && 'data' in res && Array.isArray(res.data.content)) {
        setClients(res.data.content);
        setTableData({
          page: res.data.page,
          size: res.data.size,
          totalElements: res.data.totalElements,
          totalPages: res.data.totalPages,
        });
      } else {
        setClients([]);
        setTableData({ ...tableData, totalElements: 0, totalPages: 0 });
      }
    } catch {
      setClients([]);
      setTableData({ ...tableData, totalElements: 0, totalPages: 0 });
    }
    setIsSearching(false);
  };

  const handleSort = (field: string) => {
    let newDirection: 'asc' | 'desc' = 'asc';
    
    if (sortConfig.field === field) {
      if (sortConfig.direction === 'asc') {
        newDirection = 'desc';
      } else if (sortConfig.direction === 'desc') {
        // Si ya está desc, quitar ordenamiento
        setSortConfig({ field: null, direction: null });
        setPage(0); // Resetear página al cambiar ordenamiento
        return;
      }
    }
    
    setSortConfig({ field, direction: newDirection });
    setPage(0); // Resetear página al cambiar ordenamiento
  };

  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  const isInstrumentationEnabled = config?.isInstrumentationEnabled ?? true;

  const activityOrRubroColumn = buildActivityOrRubroColumn(
    isUruguayEnabled,
    isInstrumentationEnabled,
  );

  const columns = [
    {
      name: fiscalIdLabel,
      selector: (row: any) => row.cuit,
      cell: (row: any) => (
        <TruncatedTextWithTooltip text={formatFiscalId(row.cuit)}>
          <P type='3' variant='neutral'>
            {formatFiscalId(row.cuit)}
          </P>
        </TruncatedTextWithTooltip>
      ),
      width: '10%',
      sortable: true,
      sortField: 'cuit',
    },
    {
      name: 'Razón social',
      selector: (row: any) => row.company_name,
      cell: (row: any) => (
        <TruncatedTextWithTooltip text={row.company_name}>
          <Span type='3' variant='neutral'>
            {row.company_name}
          </Span>
        </TruncatedTextWithTooltip>
      ),
      width: isInstrumentationEnabled ? '27%' : '32%',
      sortable: true,
      sortField: 'company_name',
    },
    activityOrRubroColumn,
    {
      name: 'Referido/Vendedor',
      selector: (row: any) => row.referrer_name || 'Generacion propia',
      cell: (row: any) => {
        const referidoText = row.referrer_name ? row.referrer_name : 'Generacion propia';
        return (
          <TruncatedTextWithTooltip text={referidoText}>
            <P type='3' variant='neutral'>
              {referidoText}
            </P>
          </TruncatedTextWithTooltip>
        );
      },
      width: isInstrumentationEnabled ? '15%' : '18%',
      sortable: true,
      sortField: 'referrer_name',
    },
    {
      name: 'Solicitudes',
      selector: (row: any) => row.applications_total,
      cell: (row: any) => (
        <P type='3' variant='neutral'>
          {row.applications_total}
        </P>
      ),
      center: true,
      width: isInstrumentationEnabled ? '7%' : '8%',
    },
    ...(isInstrumentationEnabled ? [{
      name: 'Instrumentadas',
      selector: (row: any) => row.applications_in_instrumentation,
      cell: (row: any) => (
        <P type='3' variant='neutral'>
          {row.applications_in_instrumentation}
        </P>
      ),
      center: true,
      width: '13%',
    }] : []),
  ];

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleRowClicked = (row: any) => {
    const listState = buildListState({
      dashboardKey: DASHBOARD_KEYS.clients,
      page,
      searchText: q,
      activeFilters,
      sortField: sortConfig.field,
      sortDirection: sortConfig.direction,
    });

    navigate(`${routes.clients}/${row.cuit}`, {
      state: { referrer_name: row.referrer_name, listState },
    });
  };

  const handleOpenModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (queryParams: Record<string, string>) => {
    setActiveFilters(queryParams);
    setPage(0); // Resetear a la primera página cuando se aplican filtros
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
  };

  const getActiveFilterLabels = (): Array<{
    type: string;
    label: string;
    filterKey: string;
  }> => {
    if (!filterData || Object.keys(activeFilters).length === 0) {
      return [];
    }

    const labels: Array<{ type: string; label: string; filterKey: string }> =
      [];

    // Solo mostrar referrerName porque es el único filtro habilitado
    if (activeFilters.referrerName) {
      const referrer = filterData.referrers.find(
        (r) => r.id.toString() === activeFilters.referrerName
      );
      if (referrer) {
        labels.push({
          type: 'Referido/Vendedor',
          label: referrer.name,
          filterKey: 'referrerName',
        });
      } else {
        labels.push({
          type: 'Referido/Vendedor',
          label: 'Desconocido',
          filterKey: 'referrerName',
        });
      }
    }

    return labels;
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterKey];
    setActiveFilters(newFilters);
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchClients();
  };

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      <Card display='flex' justifyContent='space-between' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Hola, {name}
          </H>
      </Card>
      <Card display='flex' gap='16px' alignItems='center' margin={{ top: '4' }}>
        <Button variant='secondary-1' onClick={handleOpenModal}>
          <Card display='flex' alignItems='center' gap='8px'>
            <Icon name='other_filter' variant='secondary-1' width={20} height={20} />
            <P variant='secondary-1' withMargin={false}>Filtrar clientes</P>
          </Card>
        </Button>
        <Card width='400px' display='flex' gap='16px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder={searchPlaceholder}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyup={(e) => e.keyCode === 13 && handleSearch()}
          />
          <Button
            variant='tertiary'
            size='sm'
            onClick={handleSearch}
            disabled={isSearching}
          >
            {isSearching ? <Spinner size='sm' variant='primary' /> : 'Buscar'}
          </Button>
        </Card>
      </Card>
      {Object.keys(activeFilters).length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            marginTop: '8px',
            flexWrap: 'wrap',
            backgroundColor: '#F5F7FA',
            padding: '16px 24px',
            borderRadius: '4px',
            border: '1px solid #E5E7EB',
          }}
        >
          {getActiveFilterLabels().map((filter, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {filter.filterKey === 'status' ? (
                <>
                  <P variant='neutral' withMargin={false} type='3' weight='bold'>
                    {filter.type}
                  </P>
                  <div
                    style={{
                      backgroundColor: '#A16108',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '24px',
                    }}
                  >
                    <P
                      variant='neutral'
                      modifier='inverted'
                      withMargin={false}
                      type='3'
                      weight='bold'
                      style={{ color: '#FFFFFF', fontSize: '11px', lineHeight: '16px', letterSpacing: '0.3px' }}
                    >
                      {filter.label.toUpperCase()}
                    </P>
                  </div>
                </>
              ) : (
                <>
                  <Icon
                    name='other_filter'
                    variant='secondary-1'
                    width={16}
                    height={16}
                  />
                  <P variant='neutral' withMargin={false} type='3'>
                    <span style={{ fontWeight: 'bold' }}>{filter.type}</span>{' '}
                    {filter.label}
                  </P>
                </>
              )}
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}
            onMouseEnter={() => setIsClearFiltersHovered(true)}
            onMouseLeave={() => setIsClearFiltersHovered(false)}
            onClick={handleClearFilters}
          >
            <Icon
              name='delete_outline'
              variant='neutral'
              width={20}
              height={20}
              style={{ color: activeFilters.status ? '#A16108' : '#585053' }}
            />
            <P variant='neutral' withMargin={false} type='3' style={{ color: activeFilters.status ? '#A16108' : '#585053' }}>
              Borrar filtros
            </P>
          </div>
        </div>
      )}
      {isSearching ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : (
        <Card margin={{ top: '4' }}>
          <Table
            columns={columns}
            data={clients}
            tableData={tableData}
            handleChangePage={handleChangePage}
            onRowClicked={handleRowClicked}
            onSort={handleSort}
          />
        </Card>
      )}
      
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleCloseModal}
        onApplyFilters={handleApplyFilters}
        showCommercialFilter={false}
        showProducerFilter={false}
        showReferrerFilter={true}
        showStatusFilter={false}
        clientReferred={true}
        initialFilters={activeFilters}
      />
    </Card>
  );
};

export default Clients;
